/**
 * The "Add a service from an image" form (components/apps/ImageServiceDialog): from a picked catalog entry or an image
 * typed in, to the body vfs-service's POST /image-services takes. Ports are typed as `name:port[/protocol]`, the
 * environment as `NAME=value` lines.
 */

export const NAME_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** `ui:15672, amqp:5672/tcp` → `[{ name, port, protocol }]`; throws on a part that is not one. */
export function parsePorts(text) {
  return String(text || '').split(/[\s,]+/).filter(Boolean).map((part) => {
    const match = part.match(/^([a-z0-9][a-z0-9-]*):(\d{1,5})(?:\/(http|grpc|tcp))?$/);
    if (!match) { throw new Error(`"${part}" is not name:port (optionally /http, /grpc or /tcp)`); }
    return { name: match[1], port: Number(match[2]), protocol: match[3] || 'http' };
  });
}

export const formatPorts = (ports) => (ports || []).map((p) => `${p.name}:${p.port}${p.protocol && p.protocol !== 'http' ? `/${p.protocol}` : ''}`).join(', ');

/** `NAME=value` lines → an object; blank lines and `#` comments skipped; throws on a line without `=`. */
export function parseEnv(text) {
  const env = {};
  for (const raw of String(text || '').split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) { continue; }
    const at = line.indexOf('=');
    if (at < 1) { throw new Error(`"${line}" is not NAME=value`); }
    env[line.slice(0, at).trim()] = line.slice(at + 1);
  }
  return env;
}

export const formatEnv = (env) => Object.entries(env || {}).map(([k, v]) => `${k}=${v}`).join('\n');

/** The form's fields for a catalog entry (or empty for "Any image"). */
export function formFor(entry, taken = []) {
  let name = entry ? entry.id : '';
  for (let n = 2; name && taken.includes(name); n++) { name = `${entry.id}-${n}`; }
  return {
    from: entry?.id || null,
    name,
    image: entry?.image || '',
    port: entry?.port ? String(entry.port) : '',
    protocol: entry?.protocol || 'http',
    ports: formatPorts(entry?.ports),
    env: formatEnv(entry?.env),
    volume: entry?.volume || '',
    memory: entry?.memory || '512m',
  };
}

/** The POST body from the form, or throws with what to fix. */
export function bodyOf(form, taken = []) {
  const name = String(form.name || '').trim();
  if (!NAME_RE.test(name)) { throw new Error('The name is lowercase letters, digits and - (up to 40).'); }
  if (taken.includes(name)) { throw new Error(`This project already has a service called "${name}".`); }
  if (!String(form.image || '').trim()) { throw new Error('Choose an image, e.g. "redis:8.2".'); }
  const body = { name, image: form.image.trim(), protocol: form.protocol || 'http', ports: parsePorts(form.ports), env: parseEnv(form.env) };
  if (form.from) { body.from = form.from; }
  if (String(form.port || '').trim()) { body.port = Number(form.port); }
  if (String(form.volume || '').trim()) { body.volume = form.volume.trim(); }
  if (String(form.memory || '').trim()) { body.memory = form.memory.trim(); }
  return body;
}
